import { useState, useEffect, useMemo, useRef } from "react";
import Layout from "../components/Layout";
import Pagination, { useClientPagination } from "../components/Pagination";
import PageForm from "../components/PageForm";
import WizardPopover from "../components/WizardPopover";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { slugify } from "../lib/slug";
import { toast } from "sonner";
import {
  Shield,
  Plus,
  Pencil,
  Trash2,
  X,
  Search,
  Lock,
  Users as UsersIcon,
  ClipboardList,
  Settings as SettingsIcon,
  FileText,
  CheckSquare,
  Square,
  Loader2,
  Save,
  AlertCircle,
  ChevronDown,
  ChevronRight,
} from "lucide-react";

const ICON_BY_MODULE = {
  M1: ClipboardList,
  M17: Shield,
  M19: SettingsIcon,
  M20: FileText,
};

export default function AdminRoles() {
  const { hasPerm, refreshPerms, user: currentUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | role obj
  const [editingFull, setEditingFull] = useState(false);
  const editAnchorRef = useRef(null);
  const [viewingPerms, setViewingPerms] = useState(null); // rol completo para modal de permisos
  // v2.8.8.4 · Draft compartido entre RoleWizard y RoleEditor (evita pérdida
  // de nombre/descripción/MFA al expandir del popover al editor full-page).
  const [wizardDraft, setWizardDraft] = useState(null);

  const openEditor = (target, anchorEl) => {
    editAnchorRef.current = anchorEl || null;
    setEditingFull(false);
    setEditing(target);
    setWizardDraft(null);
  };

  const closeEditor = () => {
    setEditing(null);
    setEditingFull(false);
    editAnchorRef.current = null;
    setWizardDraft(null);
  };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [r, m] = await Promise.all([
        api.get("/v1/roles"),
        api.get("/v1/roles/modules"),
      ]);
      setRoles(r.data.items || []);
      setModules(m.data.items || []);
    } catch (e) {
      toast.error("No se pudo cargar roles/módulos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  const filtered = useMemo(() => {
    if (!query) return roles;
    const q = query.toLowerCase();
    return roles.filter(r =>
      r.code.toLowerCase().includes(q) ||
      r.name.toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  }, [roles, query]);

  const pg = useClientPagination(filtered, 10);

  const handleDelete = async (role) => {
    if (role.is_system) return toast.error("No se puede eliminar un rol del sistema");
    if (!window.confirm(`¿Eliminar el rol "${role.name}"?`)) return;
    try {
      await api.delete(`/v1/roles/${role.code}`);
      toast.success("Rol eliminado");
      fetchAll();
      // Si el rol eliminado era del usuario actual, refrescar sus permisos
      if (currentUser?.role === role.code) await refreshPerms();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al eliminar");
    }
  };

  const stats = useMemo(() => ({
    total: roles.length,
    sistema: roles.filter(r => r.is_system).length,
    personalizados: roles.filter(r => !r.is_system).length,
    modulos: modules.length,
  }), [roles, modules]);

  return (
    <Layout
      title="Roles y Permisos"
      subtitle="Configuración · M17"
      actions={
        hasPerm("M17.roles.manage") && (
          <button
            onClick={(e) => openEditor("new", e.currentTarget)}
            className="neu-btn neu-btn-accent"
            style={{ padding: "8px 14px", fontSize: 13 }}
            data-testid="new-role-btn"
          >
            <Plus size={14} /> Nuevo rol
          </button>
        )
      }
    >
      <div className="space-y-6" data-testid="roles-page">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Total roles" value={stats.total} tone="navy" />
          <StatCard label="Del sistema" value={stats.sistema} tone="cyan" />
          <StatCard label="Personalizados" value={stats.personalizados} tone="yellow" />
          <StatCard label="Módulos disponibles" value={stats.modulos} tone="green" />
        </div>

        {/* Search */}
        <div className="neu-card p-3 flex items-center gap-3">
          <Search size={16} style={{ color: "#0A3747" }} />
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder="Buscar por nombre, código o descripción..."
            className="flex-1 text-sm outline-none bg-transparent"
            style={{ color: "#0F172A" }}
            data-testid="role-search"
          />
        </div>

        {/* Table */}
        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="animate-spin text-gray-400" />
          </div>
        ) : (
          <div className="neu-card overflow-hidden">
            <table className="w-full text-sm neu-table">
              <thead>
                <tr>
                  <th className="text-left">Rol</th>
                  <th className="text-left">Código</th>
                  <th className="text-left">Descripción</th>
                  <th className="text-right">Permisos</th>
                  <th className="text-center">Tipo</th>
                  <th className="text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filtered.length === 0 ? (
                  <tr><td colSpan={6} className="text-center py-10 text-gray-400">Sin roles que mostrar.</td></tr>
                ) : pg.paged.map(role => (
                  <tr key={role.code} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`role-row-${role.code}`}>
                    <td className="px-4 py-3 font-semibold text-gray-900">{role.name}</td>
                    <td className="px-4 py-3 font-mono text-xs text-[#0A3747]">{role.code}</td>
                    <td className="px-4 py-3 text-xs text-gray-600 max-w-md">{role.description || <span className="text-gray-300">—</span>}</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={async () => {
                          try {
                            const { data } = await api.get(`/v1/roles/${role.code}`);
                            setViewingPerms(data);
                          } catch (e) {
                            toast.error("No se pudieron cargar los permisos");
                          }
                        }}
                        className="inline-block bg-[#E0E621] text-[#0A3747] font-bold px-2 py-0.5 rounded-full text-xs font-mono hover:bg-[#c9cf1e] transition cursor-pointer"
                        title="Ver permisos del rol"
                        data-testid={`view-perms-${role.code}`}
                      >
                        {role.permission_count}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {role.is_system ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#0A3747] text-[#E0E621] text-[10px] font-mono uppercase tracking-wider">
                          <Lock size={10} /> Sistema
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-[10px] font-mono uppercase tracking-wider">
                          Custom
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={(e) => openEditor(role, e.currentTarget)} disabled={!hasPerm("M17.roles.manage")} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed" title="Editar" data-testid={`edit-${role.code}`}>
                        <Pencil size={14} className="text-[#0A3747]" />
                      </button>
                      <button
                        onClick={() => handleDelete(role)}
                        disabled={role.is_system || !hasPerm("M17.roles.manage")}
                        className="p-1.5 rounded hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                        title={role.is_system ? "No eliminable" : "Eliminar"}
                        data-testid={`delete-${role.code}`}
                      >
                        <Trash2 size={14} className="text-red-600" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pg.total > 0 && (
              <Pagination
                page={pg.page}
                pageSize={pg.pageSize}
                total={pg.total}
                onPageChange={pg.setPage}
                onPageSizeChange={pg.setPageSize}
                testIdPrefix="roles-pagination"
              />
            )}
          </div>
        )}
      </div>

      {editing !== null && editingFull && (
        <RoleEditor
          mode={editing === "new" ? "new" : "edit"}
          initial={editing === "new" ? wizardDraft : editing}
          modules={modules}
          onClose={closeEditor}
          onSaved={async (savedCode) => {
            closeEditor();
            fetchAll();
            if (savedCode && currentUser?.role === savedCode) await refreshPerms();
          }}
        />
      )}

      {editing !== null && !editingFull && (
        <RoleWizard
          mode={editing === "new" ? "new" : "edit"}
          initial={editing === "new" ? null : editing}
          anchorRef={editAnchorRef}
          onClose={closeEditor}
          onSaved={async (savedCode) => {
            closeEditor();
            fetchAll();
            if (savedCode && currentUser?.role === savedCode) await refreshPerms();
          }}
          onOpenFull={(draft) => {
            if (draft) setWizardDraft(draft);
            setEditingFull(true);
          }}
        />
      )}

      {viewingPerms && (
        <PermissionViewerModal
          role={viewingPerms}
          modules={modules}
          onClose={() => setViewingPerms(null)}
          onEdit={() => {
            const role = viewingPerms;
            setViewingPerms(null);
            openEditor(role, null);
            setEditingFull(true);   // permisos requiere vista completa
          }}
          canEdit={hasPerm("M17.roles.manage")}
        />
      )}
    </Layout>
  );
}

// ────────────────────────────────────────────────────────────────
// PermissionViewerModal · vista read-only agrupada por módulo
// ────────────────────────────────────────────────────────────────
function PermissionViewerModal({ role, modules, onClose, onEdit, canEdit }) {
  const rolePerms = new Set(role.permissions || []);
  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4" data-testid="perm-viewer-modal">
      <div className="absolute inset-0 bg-[#0A3747]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-lg shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-[#0A3747] to-[#135166] text-white flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] font-mono text-[#E0E621] mb-1">
              Permisos del rol
            </div>
            <h2 className="text-xl font-bold" style={{ fontFamily: "Chivo" }}>{role.name}</h2>
            <div className="text-xs font-mono text-white/70 mt-0.5">
              {role.code} · {(role.permissions || []).length} permiso{(role.permissions || []).length !== 1 ? "s" : ""} habilitado{(role.permissions || []).length !== 1 ? "s" : ""}
              {role.is_system && <span className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#E0E621]/20 text-[#E0E621]"><Lock size={10} /> Sistema</span>}
            </div>
            {role.description && <p className="text-xs text-white/80 mt-2 max-w-2xl">{role.description}</p>}
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10 rounded transition" data-testid="perm-viewer-close">
            <X size={20} />
          </button>
        </div>

        {/* Grid de módulos agrupados por categoría · v2.8.7.7 */}
        <div className="flex-1 overflow-y-auto p-6 bg-gray-50">
          {modules.length === 0 ? (
            <div className="text-center text-gray-400 py-12">Sin módulos configurados</div>
          ) : (
            (() => {
              // Agrupar módulos por categoría preservando orden de aparición
              const cats = [];
              const catMap = {};
              for (const m of modules) {
                const c = m.category || "General";
                if (!catMap[c]) { catMap[c] = []; cats.push(c); }
                catMap[c].push(m);
              }
              return (
                <div className="space-y-6">
                  {cats.map((cat) => (
                    <section key={cat} data-testid={`perm-cat-${cat}`}>
                      <div className="flex items-center gap-2 mb-2 pb-1 border-b border-gray-200">
                        <div className="text-[11px] font-bold uppercase tracking-widest text-gray-600">{cat}</div>
                        <div className="text-[10px] text-gray-400">{catMap[cat].length} módulos</div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {catMap[cat].map((m) => {
                const Icon = ICON_BY_MODULE[m.code] || Shield;
                const modulePerms = (m.options || []).map((o) => `${m.code}.${o.code}`);
                const grantedCount = modulePerms.filter((p) => rolePerms.has(p)).length;
                const totalCount = modulePerms.length;
                const allGranted = grantedCount === totalCount && totalCount > 0;
                return (
                  <div
                    key={m.code}
                    className={`bg-white border rounded-lg overflow-hidden transition ${
                      allGranted ? "border-emerald-300 shadow-sm" :
                      grantedCount === 0 ? "border-gray-200 opacity-70" : "border-amber-200"
                    }`}
                    data-testid={`perm-module-${m.code}`}
                  >
                    <div className={`px-3 py-2 flex items-center justify-between ${
                      allGranted ? "bg-emerald-50" :
                      grantedCount === 0 ? "bg-gray-50" : "bg-amber-50"
                    }`}>
                      <div className="flex items-center gap-2">
                        <Icon size={14} className={allGranted ? "text-emerald-700" : grantedCount === 0 ? "text-gray-400" : "text-amber-700"} />
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{m.name}</div>
                        </div>
                      </div>
                      <span className={`text-[10px] font-bold font-mono px-2 py-0.5 rounded-full ${
                        allGranted ? "bg-emerald-600 text-white" :
                        grantedCount === 0 ? "bg-gray-200 text-gray-500" : "bg-amber-500 text-white"
                      }`}>
                        {grantedCount}/{totalCount}
                      </span>
                    </div>
                    <div className="p-2 space-y-1">
                      {(m.options || []).map((o) => {
                        const permKey = `${m.code}.${o.code}`;
                        const granted = rolePerms.has(permKey);
                        return (
                          <div
                            key={o.code}
                            className={`flex items-start gap-2 px-2 py-1.5 rounded text-xs transition ${
                              granted ? "bg-emerald-50/60" : "bg-gray-50 text-gray-400"
                            }`}
                            data-testid={`perm-item-${permKey}`}
                          >
                            {granted
                              ? <CheckSquare size={14} className="text-emerald-600 flex-shrink-0 mt-0.5" />
                              : <Square size={14} className="text-gray-300 flex-shrink-0 mt-0.5" />
                            }
                            <div className="flex-1 min-w-0">
                              <div className={`font-medium ${granted ? "text-gray-800" : "text-gray-400"}`}>{o.label}</div>
                              {o.desc && <div className="text-[10px] text-gray-500 mt-0.5">{o.desc}</div>}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
                      </div>
                    </section>
                  ))}
                </div>
              );
            })()
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-white flex items-center justify-between">
          <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
            Vista de solo lectura · para modificar usa Editar
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-3 py-1.5 text-xs rounded border border-gray-300 hover:bg-gray-50"
              data-testid="perm-viewer-btn-close"
            >
              Cerrar
            </button>
            {canEdit && !role.is_system && (
              <button
                onClick={onEdit}
                className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white hover:bg-[#135166] flex items-center gap-1.5"
                data-testid="perm-viewer-btn-edit"
              >
                <Pencil size={12} /> Editar permisos
              </button>
            )}
            {canEdit && role.is_system && (
              <button
                onClick={onEdit}
                className="px-3 py-1.5 text-xs rounded bg-[#0A3747] text-white hover:bg-[#135166] flex items-center gap-1.5"
                data-testid="perm-viewer-btn-edit"
              >
                <Pencil size={12} /> Editar permisos
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, tone = "navy" }) {
  const styles = {
    navy:  { bg: "linear-gradient(145deg, #F1F5F9, #E4EAF0)", fg: "#0A3747", ring: "rgba(163,177,198,0.55)" },
    green: { bg: "linear-gradient(145deg, #34D399, #10B981)", fg: "#064E3B", ring: "rgba(16,185,129,0.5)" },
    cyan:  { bg: "linear-gradient(145deg, #93C5FD, #3B82F6)", fg: "#1E3A8A", ring: "rgba(59,130,246,0.5)" },
    yellow:{ bg: "linear-gradient(145deg, #EDF34C, #C4CA12)", fg: "#3F2E00", ring: "rgba(163,168,33,0.5)" },
    amber: { bg: "linear-gradient(145deg, #FCD34D, #F59E0B)", fg: "#78350F", ring: "rgba(245,158,11,0.5)" },
  };
  const st = styles[tone] || styles.navy;
  return (
    <div
      className="afc-appear rounded-2xl px-4 py-3 transition-all hover:-translate-y-0.5"
      style={{
        background: st.bg,
        boxShadow: `-6px -6px 14px rgba(255,255,255,0.75), 6px 6px 14px ${st.ring}, inset -1px -1px 3px rgba(0,0,0,0.05), inset 1px 1px 3px rgba(255,255,255,0.5)`,
      }}
    >
      <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: st.fg, opacity: 0.75 }}>{label}</div>
      <div className="text-2xl font-bold mt-1" style={{ color: st.fg, textShadow: "0 1px 0 rgba(255,255,255,0.4)" }}>{value}</div>
    </div>
  );
}

function RoleWizard({ mode, initial, anchorRef, onClose, onSaved, onOpenFull }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [mfaRequired, setMfaRequired] = useState(!!initial?.mfa_required);
  const [saving, setSaving] = useState(false);
  const isSuperAdmin = initial?.code === "super_admin";
  const isSystem = !!initial?.is_system;
  const grantedCount = initial?.permissions?.length || 0;
  const autoCode = mode === "new" ? slugify(name) : (initial?.code || "");

  // v2.8.8.4 · Al pasar al editor full-page (modo nuevo), llevamos el draft
  // ya tecleado para no perderlo. En modo edición no aplica (initial ya tiene todo).
  const openFullWithDraft = () => {
    if (onOpenFull) {
      onOpenFull({
        name: name.trim(),
        description: description.trim(),
        mfa_required: mfaRequired,
        permissions: initial?.permissions || [],
      });
    }
  };

  const doSave = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    if (mode === "new" && !autoCode) return toast.error("Nombre inválido para código");
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        permissions: initial?.permissions || [],   // preserva permisos actuales
        mfa_required: mfaRequired,
      };
      if (mode === "new") {
        body.code = autoCode;
        await api.post("/v1/roles", body);
        toast.success("Rol creado");
        onSaved(autoCode);
      } else {
        await api.put(`/v1/roles/${initial.code}`, body);
        toast.success("Rol actualizado");
        onSaved(initial.code);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <WizardPopover
      open={true}
      onOpenChange={(o) => !o && onClose()}
      anchorRef={anchorRef}
      title={mode === "new" ? "Nuevo rol" : `Editar · ${initial?.name}`}
      subtitle={mode === "new" ? "Datos básicos" : (initial?.code || "")}
      icon={Shield}
      saving={saving}
      saveLabel={mode === "new" ? "Crear" : "Guardar"}
      onSave={doSave}
      onOpenFull={openFullWithDraft}
      testid="role-wizard"
      width={460}
      steps={[
        {
          key: "info",
          title: "Rol",
          icon: Shield,
          content: (
            <div className="space-y-3">
              {isSuperAdmin && (
                <div className="p-2 rounded text-[11px] flex items-center gap-2" style={{ background: "rgba(199,255,0,0.10)", border: "1px solid rgba(199,255,0,0.35)", color: "var(--accent)" }}>
                  <AlertCircle size={13} /> Super Admin: permisos siempre totales, no editables.
                </div>
              )}
              <div>
                <label>Nombre *</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ej: Gerente de Zona Norte"
                  data-testid="role-name-input"
                  autoFocus
                />
                <div className="mt-1.5 text-[11px] flex items-center gap-1.5" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  <span style={{ color: "var(--text-muted)" }}>{mode === "new" ? "Código auto:" : "Código:"}</span>
                  <span className="px-1.5 py-0.5 rounded" style={{ background: "rgba(199, 255, 0, 0.14)", color: "var(--accent)" }} data-testid="role-code-preview" title={autoCode}>
                    {autoCode || "—"}
                  </span>
                </div>
              </div>
              <div>
                <label>Descripción</label>
                <input
                  type="text"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Breve descripción del rol"
                  data-testid="role-desc-input"
                />
              </div>
              <label className="flex items-start gap-2 cursor-pointer p-2 rounded" style={{ textTransform: "none", fontFamily: "inherit", fontWeight: 500, letterSpacing: 0, marginBottom: 0, background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
                <input
                  type="checkbox"
                  checked={mfaRequired}
                  onChange={(e) => setMfaRequired(e.target.checked)}
                  data-testid="role-mfa-required"
                  style={{ marginTop: 2 }}
                />
                <div className="flex-1">
                  <div className="text-[12px] font-semibold" style={{ color: "var(--text-main)" }}>MFA obligatorio</div>
                  <div className="text-[10.5px]" style={{ color: "var(--text-muted)" }}>Usuarios con este rol requerirán 2FA. Recomendado para roles privilegiados.</div>
                </div>
              </label>
              {mode !== "new" && (
                <div className="p-2 rounded text-[11px] flex items-center justify-between" style={{ background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
                  <span style={{ color: "var(--text-muted)" }}>
                    <Lock size={11} className="inline mr-1" /> {grantedCount} permisos otorgados
                  </span>
                  <button
                    type="button"
                    onClick={openFullWithDraft}
                    style={{ color: "var(--secondary)", textDecoration: "underline" }}
                    data-testid="role-edit-perms"
                  >
                    Editar matriz de permisos →
                  </button>
                </div>
              )}
              {isSystem && !isSuperAdmin && (
                <div className="p-2 rounded text-[10.5px]" style={{ color: "var(--text-muted)", background: "var(--bg-elevated)", border: "1px solid var(--border)" }}>
                  Este es un rol del sistema. Puedes ajustar nombre/descripción/permisos pero no eliminarlo.
                </div>
              )}
            </div>
          ),
        },
      ]}
    />
  );
}

function RoleEditor({ mode, initial, modules, onClose, onSaved }) {
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [perms, setPerms] = useState(new Set(initial?.permissions || []));
  const [mfaRequired, setMfaRequired] = useState(!!initial?.mfa_required);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  // v2.8.8.3 · Expansion state por módulo. Por defecto TODO colapsado en edición
  // para no abrumar (111 permisos); expandido al crear para descubrir la matriz.
  const [expanded, setExpanded] = useState(() => {
    const s = new Set();
    if (mode === "new") modules.forEach((m) => s.add(m.code));
    return s;
  });
  const isSystem = !!initial?.is_system;
  const isSuperAdmin = initial?.code === "super_admin";

  const toggleExpand = (code) => {
    const ns = new Set(expanded);
    if (ns.has(code)) ns.delete(code); else ns.add(code);
    setExpanded(ns);
  };
  const expandAll = () => setExpanded(new Set(modules.map((m) => m.code)));
  const collapseAll = () => setExpanded(new Set());

  // Filtro por texto: aplica sobre nombre de módulo, categoría o cualquier opción/label.
  // v2.8.8.6 · Tabla de SINÓNIMOS por categoría + prefix matching por token (>=5 chars).
  //   - "zona"/"zonas" → categoría "Gestión Geográfica" (arrastra M8/M19/M37)
  //   - "geografia"/"geografico"/"geografi..." → misma categoría por prefix match
  //   - "usuarios"/"usuario" → "Administración de Usuarios"
  //   - "operacion"/"operativo" → "Gestión de Operación"
  //   - "agente"/"agentes"/"inteligencia" → "Cerebro de Inteligencia"
  const q = search.trim().toLowerCase();
  const normalize = (s) => (s || "").toLowerCase()
    .replace(/[áàä]/g, "a").replace(/[éèë]/g, "e").replace(/[íìï]/g, "i")
    .replace(/[óòö]/g, "o").replace(/[úùü]/g, "u");
  // Sinónimos: keyword (normalizado) → categoría a arrastrar
  const CATEGORY_SYNONYMS = {
    "zona": "Gestión Geográfica",
    "zonas": "Gestión Geográfica",
    "geografia": "Gestión Geográfica",
    "geografica": "Gestión Geográfica",
    "geografico": "Gestión Geográfica",
    "geo": "Gestión Geográfica",
    "bodega": "Gestión Geográfica",
    "bodegas": "Gestión Geográfica",
    "usuario": "Administración de Usuarios",
    "usuarios": "Administración de Usuarios",
    "auditoria": "Administración de Usuarios",
    "roles": "Administración de Usuarios",
    "operacion": "Gestión de Operación",
    "operativo": "Gestión de Operación",
    "orden": "Gestión de Operación",
    "ordenes": "Gestión de Operación",
    "monitoreo": "Gestión de Operación",
    "fiscalizacion": "Gestión de Operación",
    "inventario": "Inventario y Materiales",
    "stock": "Inventario y Materiales",
    "material": "Inventario y Materiales",
    "materiales": "Inventario y Materiales",
    "agente": "Centro de Inteligencia",
    "agentes": "Centro de Inteligencia",
    "inteligencia": "Centro de Inteligencia",
    "cerebro": "Centro de Inteligencia",
    "documentacion": "Documentación y Sistema",
    "sistema": "Documentación y Sistema",
  };
  const nq = normalize(q);
  // Prefix matching: token >=5 chars ancla el resto de la palabra
  const tokenPrefixMatch = (token, text) => {
    if (!token || token.length < 5) return false;
    const t = normalize(text);
    return t.split(/\s+/).some((w) => w.startsWith(token) || token.startsWith(w));
  };
  const matchText = (text) => {
    if (!text) return false;
    const t = normalize(text);
    if (t.includes(nq)) return true;
    // token-based fallback para queries de >=5 chars (ej "geografi" matchea "geografica")
    return tokenPrefixMatch(nq, t);
  };
  // Categorías que se arrastran por sinónimo o por match textual
  const matchedCategories = useMemo(() => {
    if (!q) return new Set();
    const s = new Set();
    // 1) sinónimos exactos
    if (CATEGORY_SYNONYMS[nq]) s.add(CATEGORY_SYNONYMS[nq]);
    // 2) sinónimos por prefijo (ej. "geografi" → "geografica" → categoría)
    for (const [kw, cat] of Object.entries(CATEGORY_SYNONYMS)) {
      if (nq.length >= 4 && (kw.startsWith(nq) || nq.startsWith(kw))) s.add(cat);
    }
    // 3) match textual con la categoría misma
    for (const m of modules) {
      if (matchText(m.category)) s.add(m.category);
    }
    return s;
  }, [modules, q, nq]);

  const filtered = useMemo(() => {
    if (!q) return modules;
    return modules.filter((m) => {
      if (matchedCategories.has(m.category)) return true;
      if (matchText(m.name)) return true;
      return (m.options || []).some((o) =>
        matchText(o.label) || matchText(o.desc || "") || matchText(o.code)
      );
    });
  }, [modules, q, matchedCategories, matchText]);

  // Auto-expandir módulos que matchean la búsqueda; restaurar al limpiar.
  // v2.8.8.5 · Guardamos snapshot del estado ANTES de la primera búsqueda para
  // restaurarlo al vaciar el input (así el usuario no pierde su vista).
  const preSearchSnapshot = useRef(null);
  useEffect(() => {
    if (q) {
      if (preSearchSnapshot.current === null) {
        preSearchSnapshot.current = new Set(expanded);
      }
      setExpanded(new Set(filtered.map((m) => m.code)));
    } else if (preSearchSnapshot.current !== null) {
      setExpanded(preSearchSnapshot.current);
      preSearchSnapshot.current = null;
    }
  }, [q, filtered]);

  // Código automático: slug del nombre al crear. En edición, se mantiene el original.
  const autoCode = mode === "new" ? slugify(name) : (initial?.code || "");

  // v2.8.8.7 · REGLA DE JERARQUÍA DE PERMISOS
  // Al marcar CUALQUIER acción Nivel 2 de un módulo se activa automáticamente
  // el permiso base Nivel 1 (primera opción del catálogo, típicamente `view`).
  // Al DESMARCAR el Nivel 1 se retiran todas las acciones Nivel 2 del módulo
  // (no tiene sentido operar sin poder entrar al módulo).
  const modulesByCode = useMemo(() => {
    const m = new Map();
    for (const mod of modules) m.set(mod.code, mod);
    return m;
  }, [modules]);

  // v101.84.0 · Módulos con subgrupos independientes (M17: usuarios / roles / auditoría / notificaciones):
  //             el Nivel 1 de cada acción es el «<grupo>.view» de su grupo (misma regla que el servidor).
  const GROUPED_L1 = new Set(["M17"]);
  const level1KeyOf = (mod, key) => {
    if (key && GROUPED_L1.has(mod.code)) {
      const rest = key.slice(mod.code.length + 1);
      const grp = rest.includes(".") ? rest.split(".")[0] : "";
      if (grp && mod.options.some((o) => o.code === `${grp}.view`)) return `${mod.code}.${grp}.view`;
    }
    return `${mod.code}.${mod.options[0].code}`;
  };
  const groupOf = (mod, key) => {
    if (!GROUPED_L1.has(mod.code)) return null;
    const rest = key.slice(mod.code.length + 1);
    return rest.includes(".") ? rest.split(".")[0] : null;
  };

  const toggle = (key) => {
    const ns = new Set(perms);
    const [modCode] = key.split(".", 1);
    const mod = modulesByCode.get(modCode);
    const l1Key = mod ? level1KeyOf(mod, key) : null;
    const isL1 = key === l1Key;

    if (ns.has(key)) {
      // Desmarcar
      ns.delete(key);
      // Si es Nivel 1, arrastrar las acciones Nivel 2 (del módulo, o solo de su grupo en módulos con subgrupos)
      if (isL1 && mod) {
        const grp = groupOf(mod, key);
        for (const o of mod.options) {
          const k2 = `${mod.code}.${o.code}`;
          if (k2 === key) continue;
          if (grp ? o.code.startsWith(`${grp}.`) : true) {
            if (!grp && o.code === mod.options[0].code) continue;
            ns.delete(k2);
          }
        }
      }
    } else {
      // Marcar: auto-agregar Nivel 1 si estamos marcando un Nivel 2
      ns.add(key);
      if (!isL1 && l1Key && !ns.has(l1Key)) {
        ns.add(l1Key);
      }
    }
    setPerms(ns);
  };

  const toggleModule = (mod, allOn) => {
    const ns = new Set(perms);
    mod.options.forEach(o => {
      const k = `${mod.code}.${o.code}`;
      if (allOn) ns.delete(k); else ns.add(k);
    });
    setPerms(ns);
  };

  const moduleStats = (mod) => {
    const total = mod.options.length;
    const granted = mod.options.filter(o => perms.has(`${mod.code}.${o.code}`)).length;
    return { total, granted, full: granted === total };
  };

  const handleSave = async () => {
    if (!name.trim()) return toast.error("El nombre es obligatorio");
    if (mode === "new" && !autoCode) return toast.error("El nombre debe contener letras/números para generar un código válido");
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim(),
        permissions: Array.from(perms),
        mfa_required: mfaRequired,
      };
      if (mode === "new") {
        body.code = autoCode;
        await api.post("/v1/roles", body);
        toast.success("Rol creado");
        onSaved(autoCode);
      } else {
        await api.put(`/v1/roles/${initial.code}`, body);
        toast.success("Rol actualizado");
        onSaved(initial.code);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageForm
      title={mode === "new" ? "Nuevo rol personalizado" : `Editar rol · ${initial?.name || ""}`}
      breadcrumb={`Administración › Roles y permisos › ${mode === "new" ? "Nuevo" : "Editar"}`}
      onBack={onClose}
      maxWidth="max-w-6xl"
      testId="role-editor-page"
    >
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-testid="role-editor">

        {/* Form */}
        <div className="px-6 pt-5 pb-3 border-b border-gray-200 bg-gray-50">
          {isSuperAdmin && (
            <div className="mb-3 p-2 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800 flex items-center gap-2">
              <AlertCircle size={14} /> El rol Super Administrador siempre tiene acceso total. Los permisos no son editables.
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-2">
              <label className="block text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-1">Nombre *</label>
              <input
                type="text"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="ej: Gerente de Zona Norte"
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="role-name-input"
              />
            </div>
            <div>
              <label className="block text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-1">
                {mode === "new" ? "Código automático" : "Código"}
              </label>
              <div
                className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm font-mono bg-gray-100 text-gray-600 truncate"
                data-testid="role-code-preview"
                title={autoCode}
              >
                {autoCode || <span className="text-gray-400">— escribí un nombre —</span>}
              </div>
              {mode === "new" && (
                <div className="text-[10px] text-gray-400 mt-0.5">Se genera del nombre</div>
              )}
            </div>
            <div className="md:col-span-3">
              <label className="block text-[10px] uppercase tracking-wider font-mono text-gray-500 mb-1">Descripción</label>
              <input
                type="text"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="Breve descripción del rol..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                data-testid="role-desc-input"
              />
            </div>
            <div className="md:col-span-3">
              <label className="flex items-center gap-3 cursor-pointer p-3 bg-white border border-gray-300 rounded-md hover:border-[#0A3747]">
                <input
                  type="checkbox"
                  checked={mfaRequired}
                  onChange={(e) => setMfaRequired(e.target.checked)}
                  className="w-4 h-4 accent-[#0A3747]"
                  data-testid="role-mfa-required"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-gray-900">MFA obligatorio para este rol</div>
                  <div className="text-xs text-gray-500">Los usuarios con este rol deberán activar autenticación en dos pasos. Recomendado para roles de alto privilegio (Super Admin, Jefatura Nacional, Financiero, Auditor).</div>
                </div>
              </label>
            </div>
          </div>
        </div>

        {/* Permisos */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5 bg-gray-50">
          {/* Toolbar: búsqueda + expand/collapse */}
          <div className="sticky top-0 z-10 -mt-2 -mx-2 px-2 pt-2 pb-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative flex-1 min-w-[220px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Buscar módulo o acción (ej: crear, exportar, zonas...)"
                  className="w-full pl-8 pr-8 py-2 text-sm border border-gray-300 rounded-md bg-white focus:border-[#0A3747] outline-none"
                  data-testid="perms-search-input"
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch("")}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    aria-label="Limpiar búsqueda"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
              <button
                type="button"
                onClick={expandAll}
                className="text-[10px] uppercase tracking-wider font-mono px-3 py-2 rounded border border-gray-300 bg-white hover:border-[#0A3747]"
                data-testid="perms-expand-all"
              >
                Expandir todos
              </button>
              <button
                type="button"
                onClick={collapseAll}
                className="text-[10px] uppercase tracking-wider font-mono px-3 py-2 rounded border border-gray-300 bg-white hover:border-[#0A3747]"
                data-testid="perms-collapse-all"
              >
                Colapsar todos
              </button>
              <div className="flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-gray-600 ml-auto">
                <span data-testid="perms-count">{perms.size}</span>
                <span>/</span>
                <span>{modules.reduce((a, m) => a + m.options.length, 0)}</span>
                <span className="text-gray-400">permisos</span>
              </div>
            </div>
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-10 text-sm text-gray-400" data-testid="perms-empty">
              No hay módulos o acciones que coincidan con «{search}».
            </div>
          )}

          {(() => {
            // Agrupar módulos filtrados por categoría
            const cats = [];
            const catMap = {};
            for (const m of filtered) {
              const c = m.category || "General";
              if (!catMap[c]) { catMap[c] = []; cats.push(c); }
              catMap[c].push(m);
            }
            return cats.map((cat) => (
              <div key={cat} className="space-y-3" data-testid={`edit-cat-${cat}`}>
                <div className="flex items-center gap-2 pt-2">
                  <div className="text-[10px] uppercase tracking-[0.25em] font-bold font-mono text-[#0A3747] bg-[#E0E621]/40 px-3 py-1 rounded">{cat}</div>
                  <div className="flex-1 border-t border-dashed border-gray-300"/>
                  <div className="text-[10px] text-gray-500 font-mono">{catMap[cat].length} módulos</div>
                </div>
                {catMap[cat].map((mod) => {
                  const ms = moduleStats(mod);
                  const Icon = ICON_BY_MODULE[mod.code] || Shield;
                  const isOpen = expanded.has(mod.code);
                  // Filtrar opciones visibles cuando hay búsqueda activa
                  const visibleOpts = !q ? mod.options : mod.options.filter((o) =>
                    matchText(o.label) || matchText(o.desc || "") || matchText(o.code)
                  );
                  const optsToShow = visibleOpts.length ? visibleOpts : mod.options;
                  return (
                    <div key={mod.code} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-testid={`edit-module-${mod.code}`}>
                      <div className="bg-[#0A3747] px-4 py-2.5 flex items-center justify-between gap-2">
                        <button
                          type="button"
                          onClick={() => toggleExpand(mod.code)}
                          className="flex items-center gap-2 min-w-0 flex-1 text-left hover:opacity-90"
                          data-testid={`expand-module-${mod.code}`}
                          aria-expanded={isOpen}
                        >
                          {isOpen ? <ChevronDown size={14} className="text-[#E0E621]" /> : <ChevronRight size={14} className="text-[#E0E621]" />}
                          <Icon size={16} className="text-[#E0E621]" />
                          <span className="text-white font-semibold text-sm truncate">{mod.name}</span>
                          <span
                            className={`text-[9px] font-mono uppercase tracking-wider px-1.5 py-0.5 rounded ml-1 ${
                              ms.full ? "bg-emerald-500 text-white" : ms.granted > 0 ? "bg-amber-400 text-[#0A3747]" : "bg-gray-500 text-white/80"
                            }`}
                          >
                            {ms.granted}/{ms.total}
                          </span>
                        </button>
                        <button
                          type="button"
                          onClick={() => !isSuperAdmin && toggleModule(mod, ms.full)}
                          disabled={isSuperAdmin}
                          className={`text-[10px] uppercase tracking-wider font-mono px-2 py-1 rounded bg-[#E0E621] text-[#0A3747] font-bold hover:opacity-90 flex-shrink-0 ${isSuperAdmin ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                          data-testid={`toggle-module-${mod.code}`}
                        >
                          {ms.full ? "Quitar todos" : "Marcar todos"}
                        </button>
                      </div>
                      {isOpen && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-gray-100" data-testid={`opts-${mod.code}`}>
                          {optsToShow.map((o, idx) => {
                            const k = `${mod.code}.${o.code}`;
                            const checked = perms.has(k);
                            // Nivel 1 = PRIMERA opción del catálogo del módulo (permiso base de entrada)
                            // Todas las demás son Nivel 2 (acciones). Se calcula desde mod.options
                            // (no desde optsToShow) para que el filtrado por búsqueda no altere el nivel.
                            const isLevel1 = mod.options.length > 0 && level1KeyOf(mod, k) === k;   // v101.84.0
                            return (
                              <label
                                key={k}
                                className={`flex items-start gap-2 p-3 bg-white cursor-pointer hover:bg-gray-50 ${isSuperAdmin ? "opacity-60 cursor-not-allowed" : ""}`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => !isSuperAdmin && toggle(k)}
                                  disabled={isSuperAdmin}
                                  className="mt-0.5 w-4 h-4 accent-[#0A3747]"
                                  data-testid={`perm-${k}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-sm font-semibold text-gray-900">{o.label}</span>
                                    {isLevel1 ? (
                                      <span className="text-[8px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded bg-[#E0E621] text-[#0A3747] font-bold">
                                        Nivel 1
                                      </span>
                                    ) : (
                                      <span className="text-[8px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded bg-[#0A3747]/10 text-[#0A3747]">
                                        Nivel 2
                                      </span>
                                    )}
                                  </div>
                                  <div className="text-[11px] text-gray-500 mt-0.5">{o.desc}</div>
                                </div>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ));
          })()}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-white flex items-center justify-between gap-3 flex-wrap">
          <div className="text-xs text-gray-500">
            {isSystem && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-[#0A3747] text-[#E0E621] text-[10px] font-mono uppercase tracking-wider">
                <Lock size={10} /> Rol del sistema · no se puede eliminar
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-gray-700 hover:bg-gray-100">
              Cancelar
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-4 py-2 rounded-md bg-[#E0E621] text-[#0A3747] font-bold text-sm flex items-center gap-1.5 hover:opacity-90 transition disabled:opacity-50"
              data-testid="save-role-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              {mode === "new" ? "Crear rol" : "Guardar cambios"}
            </button>
          </div>
        </div>
      </div>
    </PageForm>
  );
}
